/**
 * Card-**pack** rolls (PRD #493): the pure, server-fed-seeded-RNG rolls that turn
 * a purchased pack into four base-odds cards and one guaranteed uncommon-or-better card.
 *
 * This is a deliberately thin, self-contained seam. The pack *sales* flow -
 * debiting gold, the unopened-pack count, the one-at-a-time flip UI, the price -
 * is a later slice (PRD slice order #4). What lives here is only the part that is
 * genuinely pure seeded randomness, so it can be unit-tested statistically now
 * exactly like the dice rolls: {@link rollPack} draws five cards from the pool at
 * the launch rarity odds. Price and
 * odds are the single tunables block the operator retunes (PRD story 34); nothing
 * here reads a clock, a socket, or gold.
 */
import {
  CARD_RARITIES,
  CHASE_CARDS,
  MAX_COPIES_PER_NAME,
  allCardIds,
} from './cardContent';
import type { CardCollectionEntry, CardRarity, DeckCardRef } from './cardContent';

/** The Core Set v1 pack price in gold. */
export const PACK_PRICE_GOLD = 3_000;

/** Cards revealed per pack. */
export const PACK_SIZE = 5;

/**
 * Per-card rarity odds before the pack-floor adjustment. One tunable
 * block; the operator retunes the economy here without touching game logic.
 */
export const PACK_RARITY_ODDS: Readonly<Record<CardRarity, number>> = {
  common: 0.66,
  uncommon: 0.25,
  rare: 0.08,
  legendary: 0.01,
};

/** Exact rarity odds for the guaranteed uncommon-or-better fifth slot. */
export const PACK_GUARANTEED_RARITY_ODDS: Readonly<
  Record<Exclude<CardRarity, 'common'>, number>
> = {
  uncommon: 25 / 34,
  rare: 8 / 34,
  legendary: 1 / 34,
};

/** Gold returned for a retained copy beyond the two-copy deck limit. */
export const CARD_DUPLICATE_REBATE_GOLD: Readonly<Record<CardRarity, number>> = {
  common: 15,
  uncommon: 50,
  rare: 150,
  legendary: 500,
};

/** Player-facing odds copy, centralized beside the tunables it discloses. */
export const PACK_ODDS_DISCLOSURE =
  'First four cards: 66% common, 25% uncommon, 8% rare, 1% legendary. '
  + 'Fifth card is guaranteed uncommon or better: exactly 25/34 uncommon, 8/34 rare, 1/34 legendary.';

/** Player-facing duplicate protection and rebate copy. */
export const PACK_DUPLICATE_DISCLOSURE =
  'Card names are protected until you own two copies. Copies beyond two stay in your Binder and rebate 15/50/150/500 gold by rarity.';

/** Player-facing copy distinguishing rarity availability from gameplay power. */
export const PACK_RARITY_FAIRNESS_DISCLOSURE =
  'Rarity controls collectability and availability, not card power: every printing of a card plays identically, and selected chase cards appear only as rare or legendary.';

/** Rarities in ascending order for the guaranteed fifth-slot roll. */
const RARITY_ORDER: readonly CardRarity[] = ['common', 'uncommon', 'rare', 'legendary'];

/** Draw one rarity from the weighted odds. */
export function rollRarity(rng: () => number): CardRarity {
  const roll = rng();
  let acc = 0;
  for (const rarity of CARD_RARITIES) {
    acc += PACK_RARITY_ODDS[rarity];
    if (roll < acc) return rarity;
  }
  return 'legendary'; // rounding guard
}

/**
 * Draw a rarity of at least uncommon for the guaranteed slot. Renormalises the
 * uncommon/rare/legendary weights so the boosted card keeps their relative odds.
 */
export function rollUncommonOrBetter(rng: () => number): Exclude<CardRarity, 'common'> {
  const eligible = RARITY_ORDER.slice(1) as readonly Exclude<CardRarity, 'common'>[];
  const roll = rng();
  let acc = 0;
  for (const rarity of eligible) {
    acc += PACK_GUARANTEED_RARITY_ODDS[rarity];
    if (roll < acc) return rarity;
  }
  return 'legendary';
}

/**
 * The card-name pool a pack draws from: every known card name. A pool entry is a
 * name; its rarity is rolled separately, so any name can surface at any rarity
 * unless its identity belongs to {@link CHASE_CARDS}. Chase identities are
 * excluded below rare while every printing of a given identity remains
 * gameplay-identical.
 */
const POOL: readonly string[] = allCardIds();
const NON_CHASE_POOL: readonly string[] = POOL.filter((id) => !CHASE_CARDS.has(id));

/** Total rebate earned by one pack against the collection before it was opened. */
export function duplicateRebateGold(
  collection: readonly CardCollectionEntry[],
  cards: readonly DeckCardRef[],
): number {
  const nameCounts = new Map<string, number>();
  for (const entry of collection) {
    nameCounts.set(entry.cardId, (nameCounts.get(entry.cardId) ?? 0) + entry.count);
  }
  let rebate = 0;
  for (const card of cards) {
    const next = (nameCounts.get(card.cardId) ?? 0) + 1;
    nameCounts.set(card.cardId, next);
    if (next > MAX_COPIES_PER_NAME) rebate += CARD_DUPLICATE_REBATE_GOLD[card.rarity];
  }
  return rebate;
}

/** Pick a uniform legal name, preferring names below the playable copy limit. */
function pickCard(
  rng: () => number,
  rarity: CardRarity,
  nameCounts: Map<string, number>,
): string {
  const pool = rarity === 'common' || rarity === 'uncommon' ? NON_CHASE_POOL : POOL;
  const protectedPool = pool.filter((id) => (nameCounts.get(id) ?? 0) < MAX_COPIES_PER_NAME);
  const candidates = protectedPool.length > 0 ? protectedPool : pool;
  const cardId = candidates[Math.floor(rng() * candidates.length)]!;
  nameCounts.set(cardId, (nameCounts.get(cardId) ?? 0) + 1);
  return cardId;
}

/**
 * Roll one pack: four cards at the base launch odds followed by an exact
 * uncommon-or-better fifth slot. Every returned ref is a legal, known card.
 */
export function rollPack(
  rng: () => number,
  collection: readonly CardCollectionEntry[] = [],
): DeckCardRef[] {
  const nameCounts = new Map<string, number>();
  for (const entry of collection) {
    nameCounts.set(entry.cardId, (nameCounts.get(entry.cardId) ?? 0) + entry.count);
  }
  const cards: DeckCardRef[] = [];
  for (let i = 0; i < PACK_SIZE - 1; i++) {
    const rarity = rollRarity(rng);
    cards.push({ cardId: pickCard(rng, rarity, nameCounts), rarity });
  }
  const guaranteedRarity = rollUncommonOrBetter(rng);
  cards.push({
    cardId: pickCard(rng, guaranteedRarity, nameCounts),
    rarity: guaranteedRarity,
  });
  return cards;
}
